import api from './api';

export type ReceiptTemplateKind =
  | 'receipt'
  | 'invoice'
  | 'label'
  | 'packing_box_label';

export type ReceiptPaperSize =
  | '58mm'
  | '80mm'
  | 'A4'
  | 'A5'
  | 'A6'
  | 'label_42x10';

export type ReceiptOrientation = 'portrait' | 'landscape';

export interface ReceiptTemplateConfig {
  store_name?: string;
  store_address?: string;
  store_phone?: string;
  store_tax_code?: string;
  logo_url?: string;
  color_primary?: string;
  footer_text?: string;
  watermark_text?: string;
  show_qr?: boolean;
  show_barcode?: boolean;
  show_signature_lines?: boolean;
}

export interface ReceiptTemplate {
  _id: string;
  tenant_id: string;
  name: string;
  kind: ReceiptTemplateKind;
  paper_size: ReceiptPaperSize;
  orientation: ReceiptOrientation;
  is_default: boolean;
  is_active: boolean;
  is_builtin: boolean;
  builtin_template_id?: string | null;
  config: ReceiptTemplateConfig;
  /** P2 canvas layout — null cho builtin chỉ dùng config form. */
  layout?: Record<string, unknown> | null;
  thumbnail_url?: string;
  version: number;
  created_at?: string;
  updated_at?: string;
}

export interface CreateReceiptTemplatePayload {
  name: string;
  kind: ReceiptTemplateKind;
  paper_size: ReceiptPaperSize;
  orientation?: ReceiptOrientation;
  builtin_template_id?: string;
  is_default?: boolean;
  config?: ReceiptTemplateConfig;
}

export interface UpdateReceiptTemplatePayload {
  name?: string;
  paper_size?: ReceiptPaperSize;
  orientation?: ReceiptOrientation;
  config?: ReceiptTemplateConfig;
  is_active?: boolean;
  /** P2 layout — null xoá hẳn (revert builtin); object replace toàn bộ. */
  layout?: Record<string, unknown> | null;
}

const BASE = '/receipt-templates';

export const receiptTemplateService = {
  list: (params?: { kind?: ReceiptTemplateKind; include_inactive?: boolean }) =>
    api
      .get<{ data: ReceiptTemplate[]; success: true }>(BASE, { params })
      .then((r) => r.data.data),

  getOne: (id: string) =>
    api
      .get<{ data: ReceiptTemplate; success: true }>(`${BASE}/${id}`)
      .then((r) => r.data.data),

  getDefault: (kind: ReceiptTemplateKind) =>
    api
      .get<{ data: ReceiptTemplate | null; success: true }>(`${BASE}/default/${kind}`)
      .then((r) => r.data.data),

  create: (body: CreateReceiptTemplatePayload) =>
    api
      .post<{ data: ReceiptTemplate; success: true }>(BASE, body)
      .then((r) => r.data.data),

  update: (id: string, body: UpdateReceiptTemplatePayload) =>
    api
      .patch<{ data: ReceiptTemplate; success: true }>(`${BASE}/${id}`, body)
      .then((r) => r.data.data),

  setDefault: (id: string) =>
    api
      .post<{ data: ReceiptTemplate; success: true }>(`${BASE}/${id}/set-default`)
      .then((r) => r.data.data),

  remove: (id: string) =>
    api.delete<{ success: true }>(`${BASE}/${id}`).then((r) => r.data),

  /**
   * Clone từ template nguồn — copy paper_size/orientation/config/layout sang
   * bản mới (is_builtin=false, is_default=false). Tên bỏ trống → server gắn
   * suffix " (bản sao)".
   */
  clone: (id: string, body?: { name?: string }) =>
    api
      .post<{ data: ReceiptTemplate; success: true }>(`${BASE}/${id}/clone`, body ?? {})
      .then((r) => r.data.data),

  /**
   * Render template với data → HTML string. Caller dùng cho iframe preview
   * hoặc browser print. Trả raw HTML (text/html), không phải JSON.
   */
  preview: (id: string, data?: Record<string, unknown>) =>
    api
      .post<string>(
        `${BASE}/${id}/preview`,
        { data },
        { responseType: 'text', headers: { Accept: 'text/html' } },
      )
      .then((r) => r.data),

  /** Upload ảnh logo / background. Trả URL public dạng `/uploads/...`. */
  uploadImage: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post<{ url: string; size: number; success: true }>(
        `${BASE}/upload-image`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data);
  },
};
